import React, { Component } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

export interface FilterCondition {
  id: string;
  field: string;
  label?: string;
  operator: string;
  value: any;
}

export interface FilterModel {
  search?: string;
  defaultField?: string;
  conditions: FilterCondition[];
}

interface MenuField {
  t: string;
  v: string;
}

interface Props {
  sdata?: any;
  value?: FilterModel;
  query?: string;
  onChange?: (model: FilterModel) => void;
  onApply?: (model: FilterModel) => void;
  onReset?: () => void;
  onClose?: () => void;
  onOpenAi?: () => void;
}

interface State {
  search: string;
  defaultField: string;
  conditions: FilterCondition[];
}

function makeId() {
  return `fc_${Math.random().toString(36).slice(2, 10)}`;
}

export default class GridAdvancedFilterPanel extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const menuFields: MenuField[] = props.sdata?.menu?.mnu || [];
    const defaultField = props.value?.defaultField || props.sdata?.menu?.def || menuFields[0]?.v || "";

    this.state = {
      search: props.value?.search || props.query || "",
      defaultField,
      conditions:
        props.value?.conditions?.length
          ? props.value.conditions
          : [
              {
                id: makeId(),
                field: defaultField,
                label: menuFields.find((m) => m.v === defaultField)?.t || "",
                operator: "contains",
                value: "",
              },
            ],
    };
  }

  private getMenuFields(): MenuField[] {
    return this.props.sdata?.menu?.mnu || [];
  }

  private getOperators() {
    return [
      { v: "contains", t: "Contains" },
      { v: "equals", t: "Equals" },
      { v: "starts", t: "Starts with" },
      { v: "ends", t: "Ends with" },
      { v: "neq", t: "Not equal" },
    ];
  }

  private emitChange(nextState?: Partial<State>) {
    const merged = { ...this.state, ...(nextState || {}) };
    const model: FilterModel = {
      search: merged.search,
      defaultField: merged.defaultField,
      conditions: merged.conditions,
    };
    this.props.onChange?.(model);
  }

  private handleSearchChange = (value: string) => {
    this.setState({ search: value }, () => this.emitChange());
  };

  private handleDefaultFieldChange = (value: string) => {
    this.setState({ defaultField: value }, () => this.emitChange());
  };

  private handleConditionChange = (id: string, key: keyof FilterCondition, value: any) => {
    const menuFields = this.getMenuFields();
    const conditions = this.state.conditions.map((c) => {
      if (c.id !== id) return c;
      const next: FilterCondition = { ...c, [key]: value };
      if (key === "field") {
        next.label = menuFields.find((m) => m.v === value)?.t || "";
      }
      return next;
    });
    this.setState({ conditions }, () => this.emitChange());
  };

  private addCondition = () => {
    const menuFields = this.getMenuFields();
    const defaultField = this.state.defaultField || menuFields[0]?.v || "";
    const conditions = [
      ...this.state.conditions,
      {
        id: makeId(),
        field: defaultField,
        label: menuFields.find((m) => m.v === defaultField)?.t || "",
        operator: "contains",
        value: "",
      },
    ];
    this.setState({ conditions }, () => this.emitChange());
  };

  private removeCondition = (id: string) => {
    const conditions = this.state.conditions.filter((c) => c.id !== id);
    this.setState(
      {
        conditions: conditions.length
          ? conditions
          : [
              {
                id: makeId(),
                field: this.state.defaultField,
                label: "",
                operator: "contains",
                value: "",
              },
            ],
      },
      () => this.emitChange()
    );
  };

  private handleApply = () => {
    const model: FilterModel = {
      search: this.state.search,
      defaultField: this.state.defaultField,
      conditions: this.state.conditions,
    };
    this.props.onApply?.(model);
  };

  private handleReset = () => {
    const menuFields = this.getMenuFields();
    const defaultField = this.props.sdata?.menu?.def || menuFields[0]?.v || "";
    this.setState(
      {
        search: "",
        defaultField,
        conditions: [
          {
            id: makeId(),
            field: defaultField,
            label: menuFields.find((m) => m.v === defaultField)?.t || "",
            operator: "contains",
            value: "",
          },
        ],
      },
      () => {
        this.emitChange();
        this.props.onReset?.();
      }
    );
  };

  render() {
    const menuFields = this.getMenuFields();
    const { onClose, onOpenAi } = this.props;
    const { search, defaultField, conditions } = this.state;

    return (
      <Box sx={{ display: "grid", gap: 1.5 }}>
        <Box
          sx={{
            display: "grid",
            gap: 1.25,
            gridTemplateColumns: {
              xs: "1fr",
              md: "2fr 1fr auto auto auto",
            },
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            label="Search"
            placeholder="Type a keyword..."
            value={search}
            onChange={(e) => this.handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, fontSize: 18 }} />,
            }}
          />

          <TextField
            select
            size="small"
            label="Default Field"
            value={defaultField}
            onChange={(e) => this.handleDefaultFieldChange(e.target.value)}
          >
            {menuFields.map((f) => (
              <MenuItem key={f.v} value={f.v}>
                {f.t}
              </MenuItem>
            ))}
          </TextField>

          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={this.addCondition}>
            Add Condition
          </Button>

          <Button variant="outlined" size="small" startIcon={<AutoAwesomeIcon />} onClick={onOpenAi}>
            AI Query
          </Button>

          {onClose ? (
            <Button variant="text" size="small" startIcon={<CloseIcon />} onClick={onClose}>
              Close
            </Button>
          ) : null}
        </Box>

        <Box sx={{ display: "grid", gap: 1 }}>
          {conditions.map((condition, index) => (
            <Box
              key={condition.id}
              sx={{
                display: "grid",
                gap: 1.25,
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "1.2fr 1fr 1.6fr auto",
                },
                alignItems: "center",
                p: 1.25,
                borderRadius: "10px",
                border: "1px solid #E2E8EE",
                bgcolor: "#FFFFFF",
              }}
            >
              <TextField
                select
                size="small"
                label={index === 0 ? "Field" : undefined}
                value={condition.field}
                onChange={(e) => this.handleConditionChange(condition.id, "field", e.target.value)}
              >
                {menuFields.map((f) => (
                  <MenuItem key={f.v} value={f.v}>
                    {f.t}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label={index === 0 ? "Operator" : undefined}
                value={condition.operator}
                onChange={(e) => this.handleConditionChange(condition.id, "operator", e.target.value)}
              >
                {this.getOperators().map((op) => (
                  <MenuItem key={op.v} value={op.v}>
                    {op.t}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                size="small"
                label={index === 0 ? "Value" : undefined}
                value={condition.value}
                onChange={(e) => this.handleConditionChange(condition.id, "value", e.target.value)}
              />

              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <IconButton size="small" onClick={() => this.removeCondition(condition.id)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          ))}
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={this.handleReset}>
            Reset
          </Button>
          <Button variant="contained" size="small" onClick={this.handleApply}>
            Apply
          </Button>
        </Stack>
      </Box>
    );
  }
}
